import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  PurchaseOrder,
  PurchaseOrderInput,
  PurchaseOrderWithUsage,
  POForecastData,
  POUsage,
  POImportResult,
  POImportMode,
  ForecastObjective,
} from '@/types/po'

const API_BASE = import.meta.env.VITE_API_URL || '/v1'

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.message || error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Custom hook for PO Tracking operations
 */
export function usePOTracking() {
  const queryClient = useQueryClient()

  // Get all years with POs
  const useYears = () =>
    useQuery({
      queryKey: ['po', 'years'],
      queryFn: () => fetchAPI<number[]>('/po/years'),
    })

  // Get all POs, optionally filtered by year
  const usePOs = (year?: number) =>
    useQuery({
      queryKey: ['po', 'all', year],
      queryFn: () => fetchAPI<PurchaseOrder[]>(year ? `/po?year=${year}` : '/po'),
    })

  // Get all POs with usage data for a specific year
  const usePOsWithUsage = (year: number) =>
    useQuery({
      queryKey: ['po', 'usage', year],
      queryFn: () => fetchAPI<PurchaseOrderWithUsage[]>(`/po/usage?year=${year}`),
      enabled: year !== undefined,
    })

  // Get single PO by ID
  const usePO = (id: string) =>
    useQuery({
      queryKey: ['po', id],
      queryFn: () => fetchAPI<PurchaseOrder>(`/po/${id}`),
      enabled: !!id,
    })

  // Get PO usage
  const usePOUsage = (id: string) =>
    useQuery({
      queryKey: ['po', id, 'usage'],
      queryFn: () => fetchAPI<POUsage>(`/po/${id}/usage`),
      enabled: !!id,
    })

  // Get forecast data
  const useForecast = (year: number, poId?: string, objective: ForecastObjective = 'balanced') =>
    useQuery({
      queryKey: ['po', 'forecast', year, poId, objective],
      queryFn: () =>
        fetchAPI<POForecastData>(
          poId
            ? `/po/forecast?year=${year}&poId=${poId}&objective=${objective}`
            : `/po/forecast?year=${year}&objective=${objective}`
        ),
      enabled: year !== undefined,
    })

  // Create PO
  const createPO = useMutation({
    mutationFn: (data: PurchaseOrderInput) =>
      fetchAPI<PurchaseOrder>('/po', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['po'] })
    },
  })

  // Update PO
  const updatePO = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PurchaseOrderInput> }) =>
      fetchAPI<PurchaseOrder>(`/po/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['po'] })
    },
  })

  // Delete PO
  const deletePO = useMutation({
    mutationFn: (id: string) =>
      fetchAPI<void>(`/po/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['po'] })
    },
  })

  // Import PO list from Excel (replaces the target year's POs)
  const importPOs = useMutation({
    mutationFn: async ({
      file,
      year,
      mode,
    }: {
      file: File
      year: number
      mode: POImportMode
    }): Promise<POImportResult> => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('year', String(year))
      formData.append('mode', mode)

      const response = await fetch(`${API_BASE}/po/import/excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Import failed' }))
        throw new Error(error.message || error.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['po'] })
    },
  })

  return {
    useYears,
    usePOs,
    usePOsWithUsage,
    usePO,
    usePOUsage,
    useForecast,
    createPO,
    updatePO,
    deletePO,
    importPOs,
  }
}
